import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { contacts } from '../utils/api';
import { formatPhoneNumber } from '../utils/phoneFormat';
import DocumentUploadForm from './Documents/DocumentUploadForm';

const SaleTransactionModal = ({ isOpen, onClose, onSubmit, coins = [] }) => {
  const isBulkImport = coins.length === 1 && coins[0]?.transactionNumber !== undefined;
  
  const initialFormState = {
    fees: '',
    contact_id: '',
    transaction_date: (coins[0]?.date || new Date().toISOString()).slice(0, 10),
    notes: '',
    coin_prices: {},
    transaction_type: 'sale'
  };

  const [formData, setFormData] = useState(initialFormState);
  const [contactsList, setContactsList] = useState([]);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    secondary_address: '',
    source: '',
    notes: '',
    contact_type: 'buyer'
  });
  const [error, setError] = useState('');
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // Initialize coin prices only when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    // Only set initial prices if they haven't been set yet
    setFormData(prev => {
      // Check if we already have prices for these coins
      const hasExistingPrices = coins.every(
        (coin, index) => prev.coin_prices[coin.id || `temp_${index}`] !== undefined
      );
      
      // If we already have prices, don't reset them
      if (hasExistingPrices) return prev;
      
      // Otherwise initialize new prices, using list_price if available
      const initialCoinPrices = coins.reduce((acc, coin, index) => ({
        ...acc,
        [coin.id || `temp_${index}`]: coin.list_price || ''
      }), {});

      return {
        ...prev,
        coin_prices: {
          ...prev.coin_prices,
          ...initialCoinPrices
        }
      };
    });
  }, [isOpen]); // Only run when modal opens/closes

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormState);
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const data = await contacts.getAll();
        setContactsList(data);
      } catch (err) {
        console.error('Failed to fetch contacts:', err);
        setError('Failed to load contacts');
      }
    };

    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDocumentUploadSuccess = (document) => {
    setUploadedDocuments(prev => [...prev, document]);
    setShowDocumentUpload(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contact_id) {
      setError('Please select or create a contact');
      return;
    }

    // Validate prices
    if (isBulkImport) {
      // For bulk import, only validate the current coin's price
      const coin = coins[0];
      const priceKey = coin.id || 'temp_0';
      const price = formData.coin_prices[priceKey];
      if (!price && price !== '0') {
        setError('Please enter a sale price for this coin');
        return;
      }
    } else {
      // For regular transactions, validate all coins have prices
      const missingPrices = Object.entries(formData.coin_prices).filter(([_, price]) => price === '');
      if (missingPrices.length > 0) {
        setError('Please enter sale prices for all coins');
        return;
      }
    }

    try {
      let transactionData;
      
      if (isBulkImport) {
        // For bulk import, we're dealing with a single coin
        const coin = coins[0];
        const priceKey = coin.id || 'temp_0';
        const price = parseFloat(formData.coin_prices[priceKey]);
        const fees = formData.fees ? parseFloat(formData.fees) : 0;
        
        transactionData = {
          coin_prices: {
            [priceKey]: {
              price: price,
              fees: fees,
              net_amount: price - fees
            }
          },
          contact_id: parseInt(formData.contact_id, 10),
          transaction_date: formData.transaction_date,
          transaction_type: 'sale',
          notes: formData.notes,
          document_ids: uploadedDocuments.map(doc => doc.id)
        };
      } else {
        // For regular transactions with multiple coins, calculate proportional fees
        const totalPrice = Object.values(formData.coin_prices).reduce((sum, price) => 
          sum + (price ? parseFloat(price) : 0), 0);
        
        const totalFees = formData.fees ? parseFloat(formData.fees) : 0;

        transactionData = {
          coin_prices: coins.reduce((acc, coin, index) => {
            const priceKey = coin.id || `temp_${index}`;
            const price = formData.coin_prices[priceKey];
            if (!price) return acc;

            const parsedPrice = parseFloat(price);
            // Calculate proportional fee for this coin
            const feeShare = totalPrice > 0 
              ? (parsedPrice / totalPrice) * totalFees 
              : totalFees / coins.length;
            
            if (coin.id) {
              acc[coin.id] = {
                price: parsedPrice,
                fees: parseFloat(feeShare.toFixed(2)),
                net_amount: parsedPrice - parseFloat(feeShare.toFixed(2))
              };
            }
            return acc;
          }, {}),
          contact_id: parseInt(formData.contact_id, 10),
          transaction_date: formData.transaction_date,
          transaction_type: 'sale',
          notes: formData.notes
        };

        // Adjust for any rounding differences
        const allocatedFees = Object.values(transactionData.coin_prices)
          .reduce((sum, { fees }) => sum + fees, 0);
        if (allocatedFees !== totalFees) {
          const diff = totalFees - allocatedFees;
          const firstCoinId = Object.keys(transactionData.coin_prices)[0];
          if (firstCoinId) {
            transactionData.coin_prices[firstCoinId].fees += parseFloat(diff.toFixed(2));
          }
        }
      }

      await onSubmit(transactionData);
      setFormData(initialFormState);
    } catch (err) {
      setError(err.message || 'Failed to create transaction');
      return;
    }
  };

  const handleNewContactSubmit = async () => {
    try {
      // Validate that either first_name or company_name is provided
      if ((!newContactData.first_name || newContactData.first_name.trim() === '') && 
          (!newContactData.company_name || newContactData.company_name.trim() === '')) {
        setError('Either first name or company name is required');
        return;
      }

      const newContact = await contacts.create(newContactData);
      setContactsList(prev => [...prev, newContact]);
      setFormData(prev => ({ ...prev, contact_id: newContact.id }));
      setShowNewContactForm(false);
      setNewContactData({
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        address: '',
        secondary_address: '',
        source: '',
        notes: '',
        contact_type: 'buyer'
      });
    } catch (err) {
      console.error('Failed to create contact:', err);
      setError('Failed to create contact');
    }
  };

  const handleNewContactChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setNewContactData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setNewContactData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[1000] flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {isBulkImport 
                ? `Record Sale - Transaction ${coins[0].transactionNumber} of ${coins[0].totalTransactions}`
                : `Record Sale - ${coins.length} Coin${coins.length > 1 ? 's' : ''}`}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <div className="flex-1 overflow-y-auto px-6 py-2 space-y-3 min-h-0">
            <div className="bg-gray-50 rounded-md overflow-hidden flex-shrink-0">
              <div className="overflow-x-auto">
                <div className="max-h-[40vh] overflow-y-auto relative">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white sticky top-0 z-10">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">ID</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Country</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Year</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Denom.</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Grade</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Cert #</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Cost</th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Sale Price *</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {coins.map((coin, index) => {
                        const priceKey = coin.id || `temp_${index}`;
                        return (
                          <tr key={priceKey} className="text-sm">
                            <td className="px-3 py-2 whitespace-nowrap w-24">{coin.user_coin_id}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-32">{coin.country}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-24">{coin.year || '-'}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-32">{coin.denomination}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-24">{coin.grade || '-'}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-24">{coin.certification_number || '-'}</td>
                            <td className="px-3 py-2 whitespace-nowrap w-24">
                              ${coin.total_cost ? parseFloat(coin.total_cost).toFixed(2) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap w-32">
                              <input
                                inputMode="decimal"
                                pattern="[0-9]*\.?[0-9]*"
                                step="0.01"
                                required
                                value={formData.coin_prices[priceKey] ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormData(prev => ({
                                    ...prev,
                                    coin_prices: {
                                      ...prev.coin_prices,
                                      [priceKey]: value
                                    }
                                  }));
                                }}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-32 sm:text-sm border-gray-300 rounded-md"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 font-medium sticky bottom-0 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan="6" className="px-3 py-2 text-right text-gray-700">Total:</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-900 w-24">
                          ${coins.reduce((sum, coin) => {
                            const value = coin.total_cost ? parseFloat(coin.total_cost) : 0;
                            return sum + value;
                          }, 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-900 w-32">
                          ${Object.values(formData.coin_prices).reduce((sum, price) => {
                            const value = price !== '' ? parseFloat(price) : 0;
                            return sum + value;
                          }, 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="transaction_date" className="block text-sm font-medium text-gray-700">
                    Transaction Date *
                  </label>
                  <div className="mt-1">
                    <input
                      type="date"
                      name="transaction_date"
                      id="transaction_date"
                      required
                      value={formData.transaction_date}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fees" className="block text-sm font-medium text-gray-700">
                    Fees
                  </label>
                  <div className="mt-1">
                    <input
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      step="0.01"
                      name="fees"
                      id="fees"
                      value={formData.fees}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact_id" className="block text-sm font-medium text-gray-700">
                    Contact *
                  </label>
                  <div className="mt-1">
                    <select
                      name="contact_id"
                      id="contact_id"
                      required
                      value={formData.contact_id}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="">Select a contact</option>
                      {contactsList.map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name}
                          {contact.company_name && ` (${contact.company_name})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setShowNewContactForm(true)}
                      className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      + Add New Contact
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Documents
                </label>
                <div className="space-y-2">
                  {uploadedDocuments.length > 0 ? (
                    <div className="space-y-2">
                      {uploadedDocuments.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <span className="text-sm text-gray-900">{doc.title}</span>
                          <button
                            type="button"
                            onClick={() => setUploadedDocuments(prev => prev.filter(d => d.id !== doc.id))}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No documents attached</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowDocumentUpload(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Upload Document
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <div className="mt-1">
                  <textarea
                    name="notes"
                    id="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={handleChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Record Sale
            </button>
          </div>
        </form>

        {showNewContactForm && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[1010] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                <h4 className="text-lg font-medium text-gray-900">New Contact</h4>
                <button
                  type="button"
                  onClick={() => setShowNewContactForm(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                <div className="space-y-4 pb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        id="first_name"
                        value={newContactData.first_name}
                        onChange={handleNewContactChange}
                        className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        id="last_name"
                        value={newContactData.last_name}
                        onChange={handleNewContactChange}
                        className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      id="company_name"
                      value={newContactData.company_name}
                      onChange={handleNewContactChange}
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={newContactData.email}
                      onChange={handleNewContactChange}
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={newContactData.phone}
                      onChange={handleNewContactChange}
                      placeholder="(555) 555-5555"
                      maxLength="14"
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Primary Address
                    </label>
                    <textarea
                      name="address"
                      id="address"
                      value={newContactData.address}
                      onChange={handleNewContactChange}
                      rows="2"
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Street address, city, state, zip code"
                    />
                  </div>

                  <div>
                    <label htmlFor="secondary_address" className="block text-sm font-medium text-gray-700">
                      Secondary Address
                    </label>
                    <textarea
                      name="secondary_address"
                      id="secondary_address"
                      value={newContactData.secondary_address}
                      onChange={handleNewContactChange}
                      rows="2"
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Optional secondary address"
                    />
                  </div>

                  <div>
                    <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                      Source
                    </label>
                    <select
                      name="source"
                      id="source"
                      value={newContactData.source}
                      onChange={handleNewContactChange}
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="">Select a source</option>
                      <option value="Referral">Referral</option>
                      <option value="Website">Website</option>
                      <option value="Reddit">Reddit</option>
                      <option value="Discord">Discord</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Trade Show">Trade Show</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact_type" className="block text-sm font-medium text-gray-700">
                      Contact Type *
                    </label>
                    <select
                      name="contact_type"
                      id="contact_type"
                      required
                      value={newContactData.contact_type}
                      onChange={handleNewContactChange}
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="buyer">Buyer</option>
                      <option value="seller">Seller</option>
                      <option value="Buyer/Seller">Buyer/Seller</option>
                      <option value="auction_house">Auction House</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      id="notes"
                      value={newContactData.notes}
                      onChange={handleNewContactChange}
                      rows="3"
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 bg-white border-t border-gray-200 p-4">
                    <button
                      type="button"
                      onClick={handleNewContactSubmit}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Create Contact
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Upload Form */}
        {showDocumentUpload && (
          <DocumentUploadForm
            open={showDocumentUpload}
            onClose={() => setShowDocumentUpload(false)}
            onSuccess={handleDocumentUploadSuccess}
            preSelectedCoins={new Set(coins.map(coin => coin.id))}
          />
        )}
      </div>
    </div>,
    document.getElementById('modal-root')
  );
};

export default SaleTransactionModal;
